import { useState, useEffect, useRef } from "react";
import styles from "./navbar.module.css";

import {
  Link,
  NavLink,
  useNavigate,
} from "react-router-dom";

import { CiSearch } from "react-icons/ci";
import { CiHeart } from "react-icons/ci";
import { PiHandbag } from "react-icons/pi";
import {
  PiUserFill,
  PiMapPin,
  PiUser,
} from "react-icons/pi";

import {
  HiMenuAlt3,
  HiX,
} from "react-icons/hi";

import { navItems } from "../../../constants/nav-links";

import { useGetCart } from "../../../queries/useCartQuery";

import authService from "../../../services/authService";

import { useQueryClient } from "@tanstack/react-query";

import toast from "react-hot-toast";

import { Image } from "../../shared/index";

import { useGetWishLists } from "../../../queries/useWishListQuery";

import GlobalProductsSearch from "../../GlobalSearch/GlobalProductsSearch";

import { useHomeQuery } from "../../../queries/useHomeQuery";

import {
  CART,
  WISHLIST_KEY,
} from "../../../constants/query.constants";


const Navbar = () => {
  const queryClient = useQueryClient();

  const searchRef = useRef(null);
  const profileRef = useRef(null);

  const [showNavbar, setShowNavbar] =
    useState(true);

  const lastScrollY = useRef(0);

  const { data } = useHomeQuery();

  const categories =
    data?.categories || [];

  const [menuOpen, setMenuOpen] =
    useState(false);

  const [searchOpen, setSearchOpen] =
    useState(false);

  const [profileOpen, setProfileOpen] =
    useState(false);

  // Initial logged-in user
  const [user, setUser] = useState(
    authService.getCurrentUser()
  );

  const isAuth = !!user;

  const {
    data: wishListItems = [],
  } = useGetWishLists();

  const {
    data: cartData = [],
  } = useGetCart();

  const cartCount = isAuth
    ? cartData?.length ?? 0
    : 0;

  const wishlistCount = isAuth
  ? wishListItems?.length ?? 0
  : 0;


  const toggleMenu = () => {
    setMenuOpen((prev) => !prev);
  };

  const closeMenu = () => {
    setMenuOpen(false);
  };

  const navigate = useNavigate();


  /*
  =====================================================
  PROFILE CLICK OUTSIDE
  =====================================================
  */

  useEffect(() => {
    const handleProfileClickOutside = (
      event
    ) => {
      if (
        profileRef.current &&
        !profileRef.current.contains(
          event.target
        )
      ) {
        setProfileOpen(false);
      }
    };

    if (profileOpen) {
      document.addEventListener(
        "mousedown",
        handleProfileClickOutside
      );
    }

    return () => {
      document.removeEventListener(
        "mousedown",
        handleProfileClickOutside
      );
    };
  }, [profileOpen]);


  /*
  =====================================================
  AUTH SYNC
  =====================================================

  IMPORTANT:
  auth-changed is used for login/logout
  in the SAME browser tab.

  storage is useful for other tabs.
  focus handles returning to the browser.
  =====================================================
  */

  useEffect(() => {
    const syncAuth = () => {
      const currentUser =
        authService.getCurrentUser();

      setUser(currentUser);
    };

    // Check immediately
    syncAuth();

    // SAME TAB LOGIN / LOGOUT
    window.addEventListener(
      "auth-changed",
      syncAuth
    );

    // OTHER TAB localStorage changes
    window.addEventListener(
      "storage",
      syncAuth
    );

    // Browser focus
    window.addEventListener(
      "focus",
      syncAuth
    );

    return () => {
      window.removeEventListener(
        "auth-changed",
        syncAuth
      );

      window.removeEventListener(
        "storage",
        syncAuth
      );

      window.removeEventListener(
        "focus",
        syncAuth
      );
    };
  }, []);


  /*
  =====================================================
  RESIZE
  =====================================================
  */

  useEffect(() => {
    const handleResize = () => {
      if (
        window.innerWidth > 768 &&
        menuOpen
      ) {
        closeMenu();
      }
    };

    window.addEventListener(
      "resize",
      handleResize
    );

    return () =>
      window.removeEventListener(
        "resize",
        handleResize
      );
  }, [menuOpen]);


  /*
  =====================================================
  BODY SCROLL
  =====================================================
  */

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow =
        "hidden";
    } else {
      document.body.style.overflow =
        "unset";
    }

    return () => {
      document.body.style.overflow =
        "unset";
    };
  }, [menuOpen]);


  /*
  =====================================================
  SEARCH CLICK OUTSIDE
  =====================================================
  */

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(
          e.target
        )
      ) {
        setSearchOpen(false);
      }
    };

    if (searchOpen) {
      document.addEventListener(
        "mousedown",
        handleClickOutside
      );
    }

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, [searchOpen]);


  /*
  =====================================================
  NAVBAR SCROLL
  =====================================================
  */

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY =
        window.scrollY;

      if (
        currentScrollY >
          lastScrollY.current &&
        currentScrollY > 100
      ) {
        setShowNavbar(false);
      } else {
        setShowNavbar(true);
      }

      lastScrollY.current =
        currentScrollY;
    };

    window.addEventListener(
      "scroll",
      handleScroll
    );

    return () => {
      window.removeEventListener(
        "scroll",
        handleScroll
      );
    };
  }, []);


  /*
  =====================================================
  LOGOUT
  =====================================================
  */

  const handleLogout = async () => {
    try {
      // Logout service
      await authService.logout();

      // Update Navbar immediately
      setUser(null);

      // IMPORTANT:
      // Notify other components
      window.dispatchEvent(
        new Event("auth-changed")
      );

      // Close running requests
      await queryClient.cancelQueries();

      // Clear cart cache
      queryClient.setQueryData(
        [CART],
        []
      );

      // Clear wishlist cache
      queryClient.setQueryData(
        [WISHLIST_KEY],
        []
      );

      // Remove cached cart queries
      queryClient.removeQueries({
        queryKey: [CART],
      });

      // Remove cached wishlist queries
      queryClient.removeQueries({
        queryKey: [WISHLIST_KEY],
      });

      toast.success(
        "Logged out successfully"
      );

      closeMenu();

      setProfileOpen(false);

      navigate("/", {
        replace: true,
      });
    } catch (error) {
      console.error(
        "Logout error:",
        error
      );

      toast.error(
        "Logout failed"
      );
    }
  };


  /*
  =====================================================
  RETURN
  =====================================================
  */

  return (
    <>
      <nav
        className={`${styles.navbar} ${
          showNavbar
            ? styles.navbarShow
            : styles.navbarHide
        }`}
      >
        <div
          className={
            styles.navbar__container
          }
        >

          {/* LOGO */}

          <Link
            to="/"
            className={
              styles.navbar__logo
            }
          >
            <Image
              src="/vitauranavlogo.jpg"
              alt="vitaura-logo"
              className={
                styles.anuradha__logo
              }
            />
          </Link>


          {/* NAV LINKS */}

          <ul
            className={
              styles.navbar__nav
            }
          >
            {navItems?.map(
              (item) => (
                <li
                  key={item?.to}
                >
                  <NavLink
                    to={item?.to}
                    end={item?.end}
                    className={({
                      isActive,
                    }) =>
                      isActive
                        ? `${styles.navbar__link} ${styles.navbar__link__active}`
                        : styles.navbar__link
                    }
                  >
                    {item?.label}
                  </NavLink>
                </li>
              )
            )}
          </ul>


          {/* ACTIONS */}

          <div
            className={
              styles.navbar__actions
            }
          >

            {/* CART */}

            <Link to="/cart">
              <button
                className={
                  styles.navbar__icon_btn
                }
                type="button"
              >
                <PiHandbag
                  size={22}
                />

                {isAuth && (
                  <span
                    className={
                      styles.navbar__badge
                    }
                  >
                    {cartCount}
                  </span>
                )}
              </button>
            </Link>


            {/* PROFILE */}

            {isAuth ? (
              <div
                className={`${styles.profileWrapper} ${
                  profileOpen
                    ? styles.profileWrapperOpen
                    : ""
                }`}
                ref={profileRef}
                onMouseEnter={() =>
                  setProfileOpen(true)
                }
                onMouseLeave={() =>
                  setProfileOpen(false)
                }
              >

                <button
                  type="button"
                  className={`${styles.navbar__icon_btn} ${styles.profileIcon} ${
                    profileOpen
                      ? styles.profileIconActive
                      : ""
                  }`}
                  onClick={() =>
                    setProfileOpen(
                      (prev) => !prev
                    )
                  }
                  aria-label="Open profile menu"
                  aria-expanded={
                    profileOpen
                  }
                >
                  <PiUserFill
                    size={23}
                  />
                </button>


                {profileOpen && (
                  <div
                    className={
                      styles.profileDropdown
                    }
                  >

                    {/* USER HEADER */}

                    <div
                      className={
                        styles.profileHeader
                      }
                    >
                      <div
                        className={
                          styles.profileAvatar
                        }
                      >
                        {(
                          user?.name ||
                          user?.username ||
                          "U"
                        )
                          .charAt(0)
                          .toUpperCase()}
                      </div>

                      <div
                        className={
                          styles.profileUserInfo
                        }
                      >
                        <h4>
                          {user?.name ||
                            user?.username ||
                            "User"}
                        </h4>

                        <p>
                          {user?.email ||
                            "Welcome back"}
                        </p>
                      </div>
                    </div>


                    {/* PROFILE MENU */}

                    <div
                      className={
                        styles.profileMenu
                      }
                    >

                      {/* MY PROFILE */}

                      <Link
                        to="/profile"
                        className={
                          styles.profileMenuItem
                        }
                        onClick={() =>
                          setProfileOpen(
                            false
                          )
                        }
                      >
                        <span
                          className={
                            styles.profileMenuIcon
                          }
                        >
                          <PiUser
                            size={19}
                          />
                        </span>

                        <span>
                          My Profile
                        </span>
                      </Link>


                      {/* MY ORDERS */}

                      <Link
                        to="/profile/order"
                        className={
                          styles.profileMenuItem
                        }
                        onClick={() =>
                          setProfileOpen(
                            false
                          )
                        }
                      >
                        <span
                          className={
                            styles.profileMenuIcon
                          }
                        >
                          <PiHandbag
                            size={19}
                          />
                        </span>

                        <span>
                          My Orders
                        </span>
                      </Link>


                      {/* MY ADDRESS */}

                      <Link
                        to="/profile/address"
                        className={
                          styles.profileMenuItem
                        }
                        onClick={() =>
                          setProfileOpen(
                            false
                          )
                        }
                      >
                        <span
                          className={
                            styles.profileMenuIcon
                          }
                        >
                          <PiMapPin
                            size={19}
                          />
                        </span>

                        <span>
                          My Address
                        </span>
                      </Link>


                      {/* WISHLIST */}

                      <Link
                        to="/profile/wishlist"
                        className={
                          styles.profileMenuItem
                        }
                        onClick={() =>
                          setProfileOpen(
                            false
                          )
                        }
                      >
                        <span
                          className={
                            styles.profileMenuIcon
                          }
                        >
                          <CiHeart
                            size={20}
                          />
                        </span>

                        <span>
                          Wishlist
                        </span>

                        {wishlistCount >
                          0 && (
                          <span
                            className={
                              styles.profileMenuBadge
                            }
                          >
                            {
                              wishlistCount
                            }
                          </span>
                        )}
                      </Link>

                    </div>


                    {/* LOGOUT */}

                    <div
                      className={
                        styles.profileLogoutSection
                      }
                    >
                      <button
                        type="button"
                        className={
                          styles.profileLogout
                        }
                        onClick={() => {
                          setProfileOpen(
                            false
                          );

                          handleLogout();
                        }}
                      >
                        Logout
                      </button>
                    </div>

                  </div>
                )}

              </div>
            ) : (

              /* NOT LOGGED IN */

              <button
                type="button"
                className={
                  styles.navbar__icon_btn
                }
                onClick={() => {
                  window.dispatchEvent(
                    new Event(
                      "open-login-otp"
                    )
                  );
                }}
                aria-label="Login"
              >
                <PiUser
                  size={23}
                />
              </button>
            )}

          </div>


          {/* MOBILE MENU BUTTON */}

          <button
            className={
              styles.navbar__menu_btn
            }
            onClick={toggleMenu}
          >
            {menuOpen ? (
              <HiX size={28} />
            ) : (
              <HiMenuAlt3
                size={28}
              />
            )}
          </button>

        </div>
      </nav>


      {/* MOBILE OVERLAY */}

      {menuOpen && (
        <div
          className={
            styles.navbar__overlay
          }
          onClick={closeMenu}
        />
      )}


      {/* MOBILE MENU */}

      <div
        className={`${styles.navbar__mobile_menu} ${
          menuOpen
            ? styles.navbar__mobile_menu__open
            : ""
        }`}
      >

        <ul
          className={
            styles.navbar__mobile_nav
          }
        >
          {navItems.map(
            (item) => (
              <li
                key={item.to}
              >
                <NavLink
                  to={item.to}
                  end={item.end}
                  className={({
                    isActive,
                  }) =>
                    isActive
                      ? `${styles.navbar__mobile_link} ${styles.navbar__mobile_link__active}`
                      : styles.navbar__mobile_link
                  }
                  onClick={closeMenu}
                >
                  {item.label}
                </NavLink>
              </li>
            )
          )}
        </ul>


        <div
          className={
            styles.navbar__mobile_actions
          }
        >

          {/* WISHLIST */}

          {isAuth ? (
            <Link
              to="/profile/wishlist"
              className={
                styles.navbar__mobile_action_btn
              }
              onClick={closeMenu}
            >
              <CiHeart
                size={22}
              />

              <span>
                Wishlist
              </span>
            </Link>
          ) : (
            <button
              type="button"
              className={
                styles.navbar__mobile_action_btn
              }
              onClick={() => {
                closeMenu();

                window.dispatchEvent(
                  new Event(
                    "open-login-otp"
                  )
                );
              }}
            >
              <CiHeart
                size={22}
              />

              <span>
                Wishlist
              </span>
            </button>
          )}


          {/* CART */}

          <Link
            to="/cart"
            className={
              styles.navbar__mobile_action_btn
            }
            onClick={closeMenu}
          >
            <PiHandbag
              size={22}
            />

            <span>
              Cart (
              {isAuth
                ? cartData.length
                : 0}
              )
            </span>
          </Link>


          {/* LOGIN / ORDERS */}

          {isAuth ? (
            <>
              <Link
                to="/profile/order"
                className={
                  styles.navbar__mobile_action_btn
                }
                onClick={closeMenu}
              >
                <PiUser
                  size={22}
                />

                <span>
                  Orders
                </span>
              </Link>

              <button
                className={
                  styles.navbar__mobile_action_btn
                }
                onClick={handleLogout}
              >
                Logout
              </button>
            </>
          ) : (
            <button
              type="button"
              className={
                styles.navbar__mobile_action_btn
              }
              onClick={() => {
                closeMenu();

                window.dispatchEvent(
                  new Event(
                    "open-login-otp"
                  )
                );
              }}
            >
              <PiUser
                size={22}
              />

              <span>
                Login
              </span>
            </button>
          )}

        </div>
      </div>
    </>
  );
};

export default Navbar;